import OutboundLink from "./OutboundLink";
import {inject, observer} from "mobx-react";
import SearchContext from "../lib/SearchContext";
import React, { useEffect, useRef, useState } from "react";
import { hash, formatMultipleItemTitle } from "../lib/utils";
import { useSessionStorage } from "../hooks/useSessionStorage";

const FALLBACK_LABEL = "Submit as new search";


export const SubmitSearchButton = ({
    ids = [],
    dsConfig = 0,
    searchParams = {},
    category = null,
    title = FALLBACK_LABEL,
    label = FALLBACK_LABEL,
    style = {},
    flat = true,
    disabled = false,
    className = "",
    onSearch = null,
    onClick = onSearch ? () => onSearch() : () => {},
    searchSessionStore: {resolveIds = null} = {},
    maxItems = 5,
  }) => {
  const key = resolveIds && ids && ids.length ? String(hash(Array.from(ids).join(":"))) : null;
  const uniqueIds = Array.from(new Set(ids));
  const [cachedLabel, setCachedLabel] = useSessionStorage(key, label);
  const validCachedLabel = cachedLabel && cachedLabel !== "null" ? cachedLabel.replace(/(query|smiles):/g, "") : null;
  const [activeLabel, setActiveLabel] = useState(resolveIds && label === FALLBACK_LABEL ? (validCachedLabel || FALLBACK_LABEL) : label);
  const localParams = category ? {tab: category} : {};
  const url = SearchContext.getAppSearchURL({searchIDs: uniqueIds, dsConfig, ...(searchParams || {})}, localParams, {withLabels: true});
  const href = url?.href || "";
  const fetching = useRef();
  const retryCount = useRef(0);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    if (!key || !resolveIds || fetching.current) {
      return;
    }
    if (activeLabel === FALLBACK_LABEL) {
      // Show raw IDs immediately rather than "Submit as new search" while the fetch is pending
      setActiveLabel(formatMultipleItemTitle(uniqueIds, maxItems).replace(/(query|smiles):/g, ""));
    }
    fetching.current = resolveIds(uniqueIds)
      .then(resolvedMap => {
        // resolveIds returns {id: entity|id} — use entity objects where available, raw id otherwise
        const labels = uniqueIds.map(id => resolvedMap[id] || id);
        // Only cache if at least one ID resolved to an entity object.
        // If all values are raw strings, the fetch failed transiently — allow retry (up to a limit).
        const anyResolved = labels.some(l => typeof l === "object");
        const newLabel = formatMultipleItemTitle(labels, maxItems).replace(/(query|smiles):/g, "");
        fetching.current = null;
        if (anyResolved) {
          retryCount.current = 0;
          setCachedLabel(newLabel);
          setActiveLabel(newLabel);
        } else if (retryCount.current < 3) {
          retryCount.current += 1;
          setRetryTick(t => t + 1);
        } else {
          // Give up after 3 retries — show raw IDs rather than looping forever
          setActiveLabel(newLabel);
        }
      })
      .catch(error => {
        console.debug("Failed to fetch metadata", error);
        fetching.current = null;
      });
  }, [key, resolveIds, retryTick])

  return (
    <OutboundLink
      key={href}
      className={`search-link${flat ? " flat-button" : ""}${className !== "" ? " " + className : ""}`}
      title={title}
      href={href}
      style={style}
      disabled={disabled || (!url && !onSearch)}
      onClick={onClick}
    >
      <span key="spyglass" className={`glyphicon glyphicon-search search-icon${disabled ? " disabled" : ""}`} onClick={onClick} />
      <span key="label" className={disabled ? "disabled" : undefined} onClick={onClick} >{activeLabel}</span>
    </OutboundLink>
  );
};

export const LabeledSubmitSearchButton = inject("searchSessionStore")(observer(SubmitSearchButton));

export default SubmitSearchButton;
